<script lang="ts">
	import ElC4SLogo from "$lib/components/c4s-logo/ElC4SLogo.svelte";
	import { appState } from "$lib/state/app-state.svelte";
	import LeftNavigation from "./LeftNavigation.svelte";
	import type { DocumentGroup } from "$lib/navigation/document-group";

	interface Props {
		groups: DocumentGroup[];
		slug: string;
	}

	const { groups, slug }: Props = $props();

	const links = $derived(appState.links);
</script>

<header class="contents lg:pointer-events-none lg:fixed lg:inset-0 lg:z-40 lg:flex">
	<div
		class="contents lg:pointer-events-auto lg:block lg:w-72 lg:overflow-y-auto lg:border-r lg:border-zinc-900/10 lg:px-6 lg:pt-4 lg:pb-8 xl:w-80 lg:dark:border-white/10">
		<a href="/" class="text-primary-600 relative hidden lg:flex dark:text-white">
			<ElC4SLogo />
			<div class="absolute top-[2px] left-43 text-sm font-bold">API</div>
		</a>
		<div
			class="fixed inset-x-0 top-0 z-50 flex h-14 items-center justify-between gap-12 bg-white/(--bg-opacity-light) px-4 backdrop-blur-xs transition sm:px-6 lg:left-72 lg:z-30 lg:px-8 xl:left-80 dark:bg-zinc-900/(--bg-opacity-dark) dark:backdrop-blur-sm"
			style="--bg-opacity-light: 50%; --bg-opacity-dark: 20%;">
			<div class="absolute inset-x-0 top-full h-px bg-zinc-900/7.5 transition dark:bg-white/7.5"></div>
			<div class="hidden lg:block lg:max-w-md lg:flex-auto">
				<button
					type="button"
					class="hidden h-8 w-full items-center gap-2 rounded-full bg-white pr-3 pl-2 text-sm text-zinc-500 ring-1 ring-zinc-900/10 transition hover:ring-zinc-900/20 lg:flex dark:bg-white/5 dark:text-zinc-400 dark:ring-white/10 dark:ring-inset dark:hover:ring-white/20"
					onclick={() => (appState.searchVisible = true)}>
					<svg viewBox="0 0 20 20" fill="none" aria-hidden="true" class="h-5 w-5 stroke-current">
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							d="M12.01 12a4.25 4.25 0 1 0-6.02-6 4.25 4.25 0 0 0 6.02 6Zm0 0 3.24 3.25">
						</path>
					</svg>
					Hledat...
					<kbd class="text-2xs ml-auto text-zinc-400 dark:text-zinc-500">
						<kbd class="font-sans">⌘</kbd>
						<kbd class="font-sans">K</kbd>
					</kbd>
				</button>
			</div>
			<div class="flex items-center gap-5 lg:hidden">
				<button
					type="button"
					class="relative flex size-6 items-center justify-center rounded-md transition hover:bg-zinc-900/5 dark:hover:bg-white/5"
					aria-label="Toggle navigation"
					onclick={() => (appState.menuVisible = !appState.menuVisible)}>
					<span class="absolute size-12 pointer-fine:hidden"> </span>
					{#if appState.menuVisible}
						<svg
							viewBox="0 0 10 9"
							fill="none"
							stroke-linecap="round"
							aria-hidden="true"
							class="w-2.5 stroke-zinc-900 dark:stroke-white"
							><path d="m1.5 1 7 7M8.5 1l-7 7"></path></svg>
					{:else}
						<svg
							viewBox="0 0 10 9"
							fill="none"
							stroke-linecap="round"
							aria-hidden="true"
							class="w-2.5 stroke-zinc-900 dark:stroke-white">
							<path d="M.5 1h9M.5 8h9M.5 4.5h9"> </path>
						</svg>
					{/if}
				</button>
				<a aria-label="Home" href="/" class="text-primary-600 relative dark:text-white">
					<ElC4SLogo />
					<div class="absolute top-[2px] left-43 text-sm font-bold">API</div>
				</a>
			</div>
			<div class="flex items-center gap-5">
				<nav class="hidden md:block">
					<ul role="list" class="flex items-center gap-8">
						<li>
							<a
								class="text-sm/5 text-zinc-600 transition hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white"
								href={links.click4survey.href}>{links.click4survey.label}</a>
						</li>
						<li>
							<a
								class="text-sm/5 text-zinc-600 transition hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white"
								href={links.plansAndPrices.href}>{links.plansAndPrices.label}</a>
						</li>
						<li>
							<a
								class="text-sm/5 text-zinc-600 transition hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white"
								href={links.support.href}>{links.support.label}</a>
						</li>
					</ul>
				</nav>
				<div class="hidden md:block md:h-5 md:w-px md:bg-zinc-900/10 md:dark:bg-white/15"></div>
				<div class="flex gap-4">
					<div class="contents lg:hidden">
						<button
							type="button"
							class="relative flex size-6 items-center justify-center rounded-md transition hover:bg-zinc-900/5 lg:hidden dark:hover:bg-white/5"
							aria-label="Hledat..."
							onclick={() => (appState.searchVisible = true)}>
							<span class="absolute size-12 pointer-fine:hidden"> </span>
							<svg
								viewBox="0 0 20 20"
								fill="none"
								aria-hidden="true"
								class="h-5 w-5 stroke-zinc-900 dark:stroke-white">
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									d="M12.01 12a4.25 4.25 0 1 0-6.02-6 4.25 4.25 0 0 0 6.02 6Zm0 0 3.24 3.25">
								</path>
							</svg>
						</button>
					</div>
					<button
						type="button"
						class="flex size-6 items-center justify-center rounded-md transition hover:bg-zinc-900/5 dark:hover:bg-white/5"
						aria-label="Přepnout do tmavého režimu"
						onclick={() => appState.toggleDarkMode()}>
						<span class="absolute size-12 pointer-fine:hidden"> </span>
						<svg
							viewBox="0 0 20 20"
							fill="none"
							aria-hidden="true"
							class="h-5 w-5 stroke-zinc-900 dark:hidden">
							<path d="M12.5 10a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0Z"> </path>
							<path
								stroke-linecap="round"
								d="M10 5.5v-1M13.182 6.818l.707-.707M14.5 10h1M13.182 13.182l.707.707M10 15.5v-1M6.11 13.889l.708-.707M4.5 10h1M6.11 6.111l.708.707">
							</path>
						</svg>
						<svg
							viewBox="0 0 20 20"
							fill="none"
							aria-hidden="true"
							class="hidden h-5 w-5 stroke-white dark:block">
							<path d="M15.224 11.724a5.5 5.5 0 0 1-6.949-6.949 5.5 5.5 0 1 0 6.949 6.949Z"> </path>
						</svg>
					</button>
				</div>
			</div>
		</div>
		<LeftNavigation groups={groups} slug={slug} class="hidden lg:mt-10 lg:block" />
	</div>
</header>
